import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ROUTE_NET_INCOME } from 'src/app/app.constants';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { NGXLogger } from 'ngx-logger';
import {
  SpendingCategory,
  TransactionItem,
  ExpensesScreenResponse,
} from '@1bill-app/services/yodlee/yodlee.types';
import _ from 'lodash';
import dayjs from 'dayjs';
import { finalize } from 'rxjs/operators';
import { getNativeMonthsOffset, getNativeWeeksOffset, getLastOfInterval } from '@1bill-app/helpers/date.helpers';
import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';

@Component({
  selector: 'app-expenses',
  templateUrl: './expenses.page.html',
  styleUrls: ['./expenses.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpensesPage implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private yodleeService: YodleeService,
    private logger: NGXLogger,
    private cdRef: ChangeDetectorRef,
    private yodleeQuery: YodleeQuery,
  ) {}

  expenseAmount: number;
  id: string;
  date = dayjs().format('YYYY-MM-DD');
  interval: 'M' | 'W' | 'Y' = 'M';
  // chartData: { amount: number; label: string; date: string }[];
  chartData: Partial<ExpensesScreenResponse>;
  spendingAmount: number;
  itemColor: string;
  availablePeriods: string[];
  spentString: string;
  intervalString: string;
  requestedPeriod: string;
  previousPeriod: string;
  categoryName: string;
  loading$ = this.yodleeQuery.isLoading$;

  subscriptionHandler = (result: {
    success: boolean;
    expensesScreenData: ExpensesScreenResponse;
  }) => {
    this.recentTransactions = result.expensesScreenData.recentTransactions;
    this.availablePeriods = result.expensesScreenData.chartData.map((cd) => cd?.date);
    this.spendingItems = result.expensesScreenData.spendingItems;
    this.spendingAmount = result.expensesScreenData.reqSpendingTotal;
    this.itemColor = result.expensesScreenData.itemColor;
    this.requestedPeriod = result.expensesScreenData.reqPeriod;
    this.previousPeriod = result.expensesScreenData.prevPeriod;
    this.categoryName = result.expensesScreenData.categoryName;
    
    // Transform data
    this.chartData = {
      chartData: result.expensesScreenData.chartData,
      spendingDifference: result.expensesScreenData.spendingDifference,
      spendingDifferenceRatio: result.expensesScreenData.spendingDifferenceRatio,
      reqPeriod: result.expensesScreenData.reqPeriod,
      prevPeriod: result.expensesScreenData.prevPeriod,
      itemColor: result.expensesScreenData.itemColor,
      categoryName: result.expensesScreenData.categoryName,
      chartMax: result.expensesScreenData.chartMax,
      chartMin: result.expensesScreenData.chartMin,
    }

    // Update label
    const getOffsetText = () => {
      const reqOffset =
        this.interval === 'M'
          ? getNativeMonthsOffset(result.expensesScreenData.reqPeriod)
          : getNativeWeeksOffset(result.expensesScreenData.reqPeriod);
      const availableLabels = [...result.expensesScreenData.chartData]
        .sort((a, b) => (a?.date > b?.date ? -1 : 1))
        .map((cd) => cd?.label);
      let intervalString: string, spentString: string;

      switch (this.interval) {
        case 'W':
          {
            intervalString = availableLabels[1]?.toLowerCase() ?? '(no previous week)';
            spentString = intervalString ? availableLabels[0].toLowerCase() : '(no previous week)';
          }
          break;
        case 'M':
          {
            spentString = `${dayjs(this.requestedPeriod).format('MMM YY')}\u2019`;
            if (reqOffset === 0) {
              intervalString = 'last month';
            } else {
              intervalString = `${dayjs(this.previousPeriod).format('MMM YY')}\u2019`;
            }
          }
          break;
        case 'Y':
          {
            spentString = 'Not implemented yet.';
            intervalString = 'Not implemented yet.';
          }
          break;
      }
      return { spentString, intervalString };
    };

    const getTextResult = getOffsetText();
    this.spentString = getTextResult.spentString;
    this.intervalString = getTextResult.intervalString;

    this.cdRef.detectChanges();
  };

  //id: string;

  defaultHref = ROUTE_NET_INCOME;
  recentTransactions: TransactionItem[];
  spendingItems: SpendingCategory[];

  async ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  fetchChartData(refreshEvent?: { target: { complete: () => void } }) {
    return this.yodleeService.getExpensesScreenData(this.id, this.date, this.interval).pipe(
      finalize(() => {
        if (refreshEvent) {
          refreshEvent.target.complete();
        }
      }),
    );
  }

  intervalChanged(to: 'M' | 'W' | 'Y') {
    this.interval = to;
    this.date = getLastOfInterval(to);
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  dateChanged(to: string) {
    this.date = to;
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  doRefresh(event: any) {
    this.fetchChartData(event).subscribe(this.subscriptionHandler);
  }
}
